"use client";

import {
  ForwardedRef,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { cn } from "@erp/shared-libs/utils";
import { BaseListProps, BaseListRef } from "./type";
import { Loader2 } from "lucide-react";

// =======================================================
// 常數定義
// =======================================================
const DEFAULT_ESTIMATE_SIZE = 60;
const DEFAULT_MAX_HEIGHT = 240;
const DEFAULT_KEY_FIELD = "id";

/**
 * 外層捲動模式的預先渲染項目數：列表上方的內容（例如可被略過的卡片）變矮而列表整體上移時，
 * 要到下一次捲動才會重新量測位置，多渲染幾筆避免這段期間底部露出空白
 */
const EXTERNAL_SCROLL_OVERSCAN = 10;

// =======================================================
// BaseList 組件實作
// =======================================================

/**
 * BaseList 組件 - 提供虛擬化列表功能
 *
 * 功能特點：
 * - 支援虛擬化渲染，提升大量資料的效能
 * - 自訂渲染函數，靈活控制列表項目樣式
 * - 內建載入中和空資料狀態顯示
 * - 支援自訂分隔線樣式
 * - 提供滾動控制方法
 *
 * @template T - 列表項目的資料類型
 */
function BaseListInner<T>(props: BaseListProps<T>, ref: ForwardedRef<BaseListRef>) {
  const {
    data,
    renderItem,
    className,
    itemClassName,
    isLoading = false,
    loadingContent,
    emptyContent,
    estimateSize = DEFAULT_ESTIMATE_SIZE,
    maxHeight = DEFAULT_MAX_HEIGHT,
    keyField = DEFAULT_KEY_FIELD as keyof T,
    enableVirtualization = true,
    containerStyle,
    showDivider = true,
    showLastDivider = false,
    onLoadMore,
    hasNextPage = false,
    isFetchingNextPage = false,
    scrollElement,
  } = props;

  // =======================================================
  // Refs
  // =======================================================
  const parentRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // =======================================================
  // 外層捲動模式
  // =======================================================
  /**
   * 是否由外層容器負責捲動（有傳 scrollElement 就是，即使容器掛載當下還是 null）
   */
  const isExternalScroll = scrollElement !== undefined;

  /**
   * 列表頂端距離外層捲動內容頂端的距離（px），交給虛擬化的 scrollMargin 換算可見範圍
   */
  const [scrollMargin, setScrollMargin] = useState(0);

  /**
   * 量測列表在外層捲動內容中的位置；值不變時不更新 state，避免多餘重繪
   */
  const measureScrollMargin = useCallback(() => {
    const listElement = parentRef.current;
    if (!scrollElement || !listElement) return;
    const next = Math.round(
      listElement.getBoundingClientRect().top -
        scrollElement.getBoundingClientRect().top +
        scrollElement.scrollTop
    );
    setScrollMargin((prev) => (prev === next ? prev : next));
  }, [scrollElement]);

  /**
   * 每次渲染後重新量測位置（列表上方內容變動時多半會帶動列表重繪）
   */
  useLayoutEffect(() => {
    measureScrollMargin();
  });

  // =======================================================
  // Infinite scroll — intersection observer on sentinel
  // =======================================================
  useEffect(() => {
    if (!onLoadMore || !hasNextPage) return;
    if (isExternalScroll && !scrollElement) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onLoadMore();
      },
      { root: isExternalScroll ? scrollElement : parentRef.current, threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [onLoadMore, hasNextPage, isExternalScroll, scrollElement]);

  // =======================================================
  // 虛擬化配置
  // =======================================================
  // eslint-disable-next-line react-hooks/incompatible-library
  const virtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => (isExternalScroll ? (scrollElement ?? null) : parentRef.current),
    estimateSize: () => estimateSize,
    enabled: enableVirtualization,
    scrollMargin: isExternalScroll ? scrollMargin : 0,
    overscan: isExternalScroll ? EXTERNAL_SCROLL_OVERSCAN : undefined,
    // React 19 不允許在 render 期間呼叫 flushSync，改用 queueMicrotask 延遲執行
    observeElementRect: (instance, cb) => {
      const el = instance.scrollElement;
      if (!el) return;
      const ro = new ResizeObserver(() => {
        queueMicrotask(() => cb({ width: el.clientWidth, height: el.clientHeight }));
      });
      ro.observe(el);
      return () => ro.disconnect();
    },
    observeElementOffset: (instance, cb) => {
      const el = instance.scrollElement;
      if (!el) return;
      const handler = () => {
        // 外層捲動模式：列表上方的內容可能已變動，捲動時順便校正列表位置
        if (isExternalScroll) measureScrollMargin();
        queueMicrotask(() => cb(el.scrollTop, false));
      };
      el.addEventListener("scroll", handler, { passive: true });
      return () => el.removeEventListener("scroll", handler);
    },
  });

  const items = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  /**
   * 第一個可見項目相對列表頂端的位移（虛擬化的 start 含 scrollMargin，要扣回來）
   */
  const firstItemOffset = (items[0]?.start ?? 0) - (isExternalScroll ? scrollMargin : 0);

  // =======================================================
  // 計算容器高度
  // =======================================================
  const containerHeight = useMemo(() => {
    if (!enableVirtualization || isExternalScroll) {
      return "auto";
    }
    return totalSize > maxHeight ? maxHeight : totalSize;
  }, [enableVirtualization, isExternalScroll, totalSize, maxHeight]);

  // =======================================================
  // 暴露方法給父組件
  // =======================================================
  useImperativeHandle(
    ref,
    () => ({
      scrollToIndex: (index: number) => {
        if (index >= 0 && index < data.length) {
          virtualizer.scrollToIndex(index, { align: "start" });
        }
      },
      getBoundingClientRect: () => {
        return parentRef.current?.getBoundingClientRect() ?? new DOMRect();
      },
      scrollToTop: () => {
        parentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      },
    }),
    [data.length, virtualizer]
  );

  // =======================================================
  // Render 載入中狀態
  // =======================================================
  if (isLoading) {
    return (
      <div className={cn("flex h-20 items-center justify-center", className)}>
        {loadingContent ?? <Loader2 className="text-muted-foreground size-5 animate-spin" />}
      </div>
    );
  }

  // =======================================================
  // Render 空資料狀態
  // =======================================================
  if (data.length === 0) {
    return (
      <div className={cn("text-muted-foreground flex h-20 items-center justify-center", className)}>
        {emptyContent ?? "尚無資料"}
      </div>
    );
  }

  // =======================================================
  // Render 列表（非虛擬化模式）
  // =======================================================
  if (!enableVirtualization) {
    return (
      <div ref={parentRef} className={cn("space-y-0", className)} style={containerStyle}>
        {data.map((item, index) => {
          const key = item[keyField] as string | number;
          const isLast = index === data.length - 1;

          return (
            <div
              key={key}
              className={cn(
                "group flex-1",
                showDivider && (!isLast || showLastDivider) && "border-b",
                itemClassName
              )}
            >
              {renderItem(item, index)}
            </div>
          );
        })}
        <div ref={sentinelRef} />
        {isFetchingNextPage && (
          <div className="flex justify-center py-2">
            <Loader2 className="text-muted-foreground size-4 animate-spin" />
          </div>
        )}
      </div>
    );
  }

  // =======================================================
  // Render 列表（虛擬化模式）
  // =======================================================
  return (
    <div
      ref={parentRef}
      className={cn(!isExternalScroll && "overflow-auto", className)}
      style={{
        height: containerHeight,
        // 外層捲動模式高度跟著內容走，不能用 strict（含尺寸與繪製裁切），否則底部的載入 spinner 會被裁掉
        contain: isExternalScroll ? undefined : "strict",
        ...containerStyle,
      }}
    >
      <div
        style={{
          position: "relative",
          height: `${totalSize}px`,
          width: "100%",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            transform: `translateY(${firstItemOffset}px)`,
          }}
        >
          {items.map((virtualRow) => {
            const item = data[virtualRow.index];
            const isLast = virtualRow.index === data.length - 1;

            return (
              <div
                key={virtualRow.key}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                className={cn(
                  "group flex-1",
                  showDivider && (!isLast || showLastDivider) && "border-b",
                  itemClassName
                )}
              >
                {renderItem(item, virtualRow.index)}
              </div>
            );
          })}
        </div>
      </div>
      <div ref={sentinelRef} />
      {isFetchingNextPage && (
        <div className="flex justify-center py-2">
          <Loader2 className="text-muted-foreground size-4 animate-spin" />
        </div>
      )}
    </div>
  );
}

// =======================================================
// 匯出 Memoized 組件
// =======================================================
export const BaseList = memo(forwardRef(BaseListInner)) as <T>(
  props: BaseListProps<T> & { ref?: ForwardedRef<BaseListRef> }
) => ReturnType<typeof BaseListInner>;
